import { FIELD_LABELS } from '../email/fieldDictionary';
import type { Company } from '../shared/companies';
import { wallClockDateTime } from '../shared/clientTime';

/**
 * Historia zmian prezlecenia po ludzku.
 *
 * `PreOrderChange` trzyma nazwy kolumn i wartości tekstem — tak, jak zapisał
 * je serwer (`loadingDate — → 2026-10-03T09:30:00.000Z`, `companyId — → 2`,
 * `utworzenie — → MANUAL`). To są dane audytu i zostają w bazie bez zmian;
 * tłumaczymy je dopiero na ekranie (e2e na dev, 28.09.2026).
 */

/** Pola nagłówka (ręczne wprowadzenie) i zdarzenia koperty. */
const HEADER_LABELS: Record<string, string> = {
  companyId: 'Spółka',
  clientName: 'Klient',
  relation: 'Relacja',
  loadingCity: 'Miasto załadunku',
  unloadingCity: 'Miasto rozładunku',
  loadingCountry: 'Kraj załadunku',
  loadingPostalCode: 'Kod pocztowy załadunku',
  unloadingCountry: 'Kraj rozładunku',
  unloadingPostalCode: 'Kod pocztowy rozładunku',
  loadingDate: 'Data załadunku',
  unloadingDate: 'Data rozładunku',
  cargo: 'Ładunek',
  weight: 'Waga (kg)',
  pallets: 'Palety',
  price: 'Cena',
  currency: 'Waluta',
  responsibleDept: 'Dział',
  customerOrderNumber: 'Nr zamówienia klienta',
  requiredVehicleType: 'Wymagany typ pojazdu',
  utworzenie: 'Utworzenie',
  akceptacja: 'Akceptacja — numer zlecenia',
  // Kopia z porównaniem (makieta back office v2, 20.10.2026).
  copiedFrom: 'Skopiowane z prezlecenia',
  copiedTo: 'Skopiowano do prezlecenia',
  copyConfirmed: 'Potwierdzone przy kopii bez zmian',
  // Obieg CS → Administracja (makieta back office v2, fala B, 21.10.2026).
  intakeStage: 'Obieg CS → Administracja',
  viaWriteStatus: 'Zapis do VIA',
  // Kontrahent z kartoteki (makieta back office v2, fala B, 21.10.2026).
  contractorId: 'Kontrahent z kartoteki',
};

/** Etapy obiegu (`PreOrderChange.intakeStage`) — kody z API, na ekranie słowem. */
const INTAKE_STAGE_LABELS: Record<string, string> = {
  cs: 'u CS',
  administration: 'u Administracji',
  returned_to_cs: 'zwrócone do CS z pytaniem',
};

/** Stan zapisu do VIA cofnięty zwrotem do CS — kody jak na ekranie zlecenia. */
const WRITE_STATE_LABELS: Record<string, string> = {
  DRAFT: 'szkic',
  APPROVED: 'zatwierdzone do zapisu',
  FAILED: 'błąd zapisu',
};

/**
 * Wpisy-zdarzenia z jedną wartością (bez „stare → nowe”): utworzenie,
 * akceptacja i ślad kopii.
 */
export const SINGLE_VALUE_FIELDS: readonly string[] = [
  'utworzenie',
  'akceptacja',
  'copiedFrom',
  'copiedTo',
  'copyConfirmed',
];

const DATE_FIELDS = new Set(['loadingDate', 'unloadingDate']);

/** Skąd przyszła zmiana (`PreOrderChange.source`). */
const SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'ręcznie',
  AI: 'ekstrakcja AI',
  RULE: 'reguła',
  VIA: 'VIA',
  COPY: 'kopia z porównaniem',
};

export function changeFieldLabel(field: string): string {
  return HEADER_LABELS[field] ?? FIELD_LABELS[field] ?? field;
}

export function changeSourceLabel(source?: string | null): string {
  if (!source) return '';
  return SOURCE_LABELS[source] ?? source;
}

/**
 * Wartość do pokazania. Nieznanej spółki nie ukrywamy — pokazujemy numer,
 * żeby było widać, że wpis wskazuje coś, czego nazwy nie znamy. Tak samo
 * kontrahent (21.10.2026): nazwa z API (`contractorNames`), a usunięty
 * z kartoteki — numerem.
 */
export function changeValueLabel(
  field: string,
  value: string | null | undefined,
  companies: ReadonlyArray<Company>,
  contractorNames: Readonly<Record<string, string>> = {},
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (DATE_FIELDS.has(field)) return wallClockDateTime(value);
  if (field === 'companyId') {
    const company = companies.find((c) => String(c.id) === value);
    return company ? company.name : `spółka #${value}`;
  }
  if (field === 'contractorId') return contractorNames[value] ?? `kontrahent #${value}`;
  if (field === 'utworzenie') {
    if (value === 'COPY') return 'kopia innego prezlecenia, po porównaniu pól';
    return value === 'MANUAL' ? 'wpisane ręcznie' : `źródło: ${changeSourceLabel(value)}`;
  }
  if (field === 'copiedFrom' || field === 'copiedTo') return `prezlecenie #${value}`;
  if (field === 'intakeStage') return INTAKE_STAGE_LABELS[value] ?? value;
  if (field === 'viaWriteStatus') return WRITE_STATE_LABELS[value] ?? value;
  return value;
}

/**
 * Miejsce punktu prezlecenia: „PL 60-001 Poznań” — kraj, kod i miasto, ile
 * wpisano (kraj i kod od 06.10.2026); nic — kreska.
 */
export function pointPlace(r: Record<string, unknown>, which: 'loading' | 'unloading'): string {
  const parts = [r[`${which}Country`], r[`${which}PostalCode`], r[`${which}City`]].filter(
    (v): v is string => typeof v === 'string' && v.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

/**
 * Prezlecenie wpisane z ręki: bez maila źródłowego i bez koperty ekstrakcji.
 * Panel „Pola wyciągnięte przez AI” pokazywał mu „0/7” i same kreski, choć
 * nagłówek był kompletny (e2e na dev, 28.09.2026) — nie ma czego oceniać.
 */
export const isManualPreOrder = (r: Record<string, unknown>): boolean =>
  r.sourceEmailId == null && r.extractedFields == null;

